import React from 'react';
import { PlatformStats, PLATFORM_ICONS } from '../types';

interface PlatformStatsCardProps {
  stats: PlatformStats;
  isLoading?: boolean;
}

const PlatformStatsCard: React.FC<PlatformStatsCardProps> = ({
  stats,
  isLoading = false
}) => {
  const getPlatformIcon = (platform: string) => {
    return PLATFORM_ICONS[platform as keyof typeof PLATFORM_ICONS] || '📱';
  };

  const getPlatformDisplay = (platform: string) => {
    const displays = {
      windows: 'Windows',
      mac: 'macOS',
      ios: 'iOS',
      ipad: 'iPadOS',
      android: 'Android'
    };
    return displays[platform as keyof typeof displays] || platform;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Chưa có';
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Thống kê theo nền tảng</h3>
        <div className="animate-pulse">
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="flex items-center space-x-4">
                <div className="w-8 h-8 bg-gray-200 rounded"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const platforms = Object.keys(stats);

  if (platforms.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Thống kê theo nền tảng</h3>
        <div className="text-center py-8">
          <div className="text-gray-500 text-lg mb-2">📊</div>
          <p className="text-gray-500">Chưa có dữ liệu thống kê</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-4">Thống kê theo nền tảng</h3>
      
      <div className="space-y-4">
        {platforms.map(platform => {
          const platformStats = stats[platform];
          
          return (
            <div key={platform} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <span className="text-2xl">{getPlatformIcon(platform)}</span>
                <div>
                  <h4 className="font-medium text-gray-900">
                    {getPlatformDisplay(platform)}
                  </h4>
                  <p className="text-sm text-gray-500">
                    {platformStats.total_versions} phiên bản
                  </p>
                </div>
              </div>
              
              <div className="text-right">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-900">
                    {platformStats.latest_version ? `v${platformStats.latest_version}` : 'Chưa có'}
                  </span>
                  {platformStats.latest_mandatory && (
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                      Bắt buộc
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Cập nhật: {formatDate(platformStats.last_updated)}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-2 gap-4 text-center">
          <div>
            <div className="text-2xl font-bold text-blue-600">
              {platforms.length}
            </div>
            <div className="text-sm text-gray-500">Nền tảng</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-green-600">
              {Object.values(stats).reduce((sum, stat) => sum + stat.total_versions, 0)}
            </div>
            <div className="text-sm text-gray-500">Tổng phiên bản</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlatformStatsCard;
